import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import styled from 'styled-components';
import { CheckCircle, XCircle, Loader, Clock, AlertCircle } from 'lucide-react';
import { theme } from '@styles/theme';
const Container = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${({ $size }) => ($size === 'sm' ? theme.spacing.xs : theme.spacing.sm)};
`;
const StatusRow = styled.div `
  display: flex;
  align-items: center;
  gap: ${({ $size }) => ($size === 'sm' ? theme.spacing.xs : theme.spacing.sm)};
  font-size: ${({ $size }) => {
    switch ($size) {
        case 'sm':
            return theme.typography.fontSize.sm;
        case 'lg':
            return theme.typography.fontSize.lg;
        default:
            return theme.typography.fontSize.base;
    }
}};
`;
const StatusIcon = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $status }) => {
    switch ($status) {
        case 'success':
            return theme.colors.status.success;
        case 'failed':
            return theme.colors.status.error;
        case 'building':
        case 'deploying':
            return theme.colors.status.warning;
        default:
            return theme.colors.text.secondary;
    }
}};
`;
const StatusText = styled.span `
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${({ $status }) => {
    switch ($status) {
        case 'success':
            return theme.colors.status.success;
        case 'failed':
            return theme.colors.status.error;
        case 'building':
        case 'deploying':
            return theme.colors.status.warning;
        default:
            return theme.colors.text.secondary;
    }
}};
`;
const StepsContainer = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  padding-left: ${theme.spacing.md};
  border-left: 2px solid ${theme.colors.border.secondary};
`;
const StepRow = styled.div `
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-size: ${theme.typography.fontSize.sm};
  color: ${({ $active, $completed, $failed }) => {
    if ($failed)
        return theme.colors.status.error;
    if ($completed)
        return theme.colors.status.success;
    if ($active)
        return theme.colors.text.primary;
    return theme.colors.text.secondary;
}};
  opacity: ${({ $active, $completed, $failed }) => ($active || $completed || $failed ? 1 : 0.6)};
`;
const StepIcon = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${({ $completed, $active, $failed }) => {
    if ($failed)
        return theme.colors.status.error;
    if ($completed)
        return theme.colors.status.success;
    if ($active)
        return theme.colors.accent.primary;
    return theme.colors.background.secondary;
}};
  color: white;
`;
const ErrorMessage = styled.div `
  padding: ${theme.spacing.sm};
  background-color: ${theme.colors.status.error}10;
  border: 1px solid ${theme.colors.status.error}40;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.status.error};
`;
const STEP_LABELS = {
    pending: 'Initializing deployment',
    create_ecr_repo: 'Creating container registry',
    build_image: 'Building Docker image',
    create_efs_access_point: 'Setting up storage',
    create_service: 'Deploying application',
    create_dns_record: 'Configuring domain',
    health_check: 'Running health checks',
    complete: 'Deployment complete',
    failed: 'Deployment failed',
};
const STEPS_ORDER = [
    'pending',
    'create_ecr_repo',
    'build_image',
    'create_efs_access_point',
    'create_service',
    'create_dns_record',
    'health_check',
    'complete',
];
export function DeploymentStatus({ deployment, showSteps = false, size = 'md' }) {
    const iconSize = size === 'sm' ? 14 : size === 'lg' ? 20 : 16;
    const statusIcon = useMemo(() => {
        switch (deployment.status) {
            case 'success':
                return _jsx(CheckCircle, { size: iconSize });
            case 'failed':
                return _jsx(XCircle, { size: iconSize });
            case 'building':
            case 'deploying':
                return _jsx(Loader, { size: iconSize, className: "animate-spin" });
            default:
                return _jsx(Clock, { size: iconSize });
        }
    }, [deployment.status, iconSize]);
    const statusLabel = useMemo(() => {
        switch (deployment.status) {
            case 'pending':
                return 'Pending';
            case 'building':
                return 'Building';
            case 'deploying':
                return 'Deploying';
            case 'success':
                return 'Deployed Successfully';
            case 'failed':
                return 'Deployment Failed';
            default:
                return deployment.status;
        }
    }, [deployment.status]);
    const renderSteps = () => {
        if (!showSteps)
            return null;
        const currentStepIndex = STEPS_ORDER.indexOf(deployment.currentStep);
        return (_jsx(StepsContainer, { children: STEPS_ORDER.filter(step => step !== 'failed').map((step, index) => {
                const isCompleted = index < currentStepIndex || deployment.status === 'success';
                const isActive = index === currentStepIndex && deployment.status !== 'failed';
                const isFailed = deployment.status === 'failed' && index === currentStepIndex;
                return (_jsxs(StepRow, { "$active": isActive, "$completed": isCompleted, "$failed": isFailed, children: [_jsx(StepIcon, { "$completed": isCompleted, "$active": isActive, "$failed": isFailed, children: isCompleted ? (_jsx(CheckCircle, { size: 10 })) : isFailed ? (_jsx(XCircle, { size: 10 })) : isActive ? (_jsx(Loader, { size: 10, className: "animate-spin" })) : (_jsx("span", { style: { fontSize: '8px' }, children: index + 1 })) }), _jsx("span", { children: STEP_LABELS[step] })] }, step));
            }) }));
    };
    return (_jsxs(Container, { "$size": size, children: [_jsxs(StatusRow, { "$size": size, children: [_jsx(StatusIcon, { "$status": deployment.status, "$size": size, children: statusIcon }), _jsx(StatusText, { "$status": deployment.status, children: statusLabel })] }), renderSteps(), deployment.errorMessage && (_jsxs(ErrorMessage, { children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: theme.spacing.xs, marginBottom: theme.spacing.xs }, children: [_jsx(AlertCircle, { size: 14 }), _jsx("strong", { children: "Error Details" })] }), deployment.errorMessage] })), deployment.deploymentUrl && deployment.status === 'success' && (_jsxs("div", { style: { fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }, children: ["Deployed to:", ' ', _jsx("a", { href: deployment.deploymentUrl, target: "_blank", rel: "noopener noreferrer", style: { color: theme.colors.accent.primary, textDecoration: 'none' }, children: deployment.deploymentUrl })] }))] }));
}
